import { Flex, Popover } from 'antd';
import { DateTime, WeekdayNumbers } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';

import {
    BORDER_MAIN,
    COLOR_WEEKEND,
    COUNT_DAY_IN_WEEK,
    FORMAT_DATE,
    FORMAT_DATE_DISPLAY_DAY,
    MAXIMUM_COUNT_FOR_SHOWING_TASK,
} from '../const';
import { RootState } from '../store';
import { ItemTaskType, ItemType } from '../types';
import CalendarItemMonth from './calendarItemMonth';
import CalendarTaskMonth from './calendarTaskMonth';

interface ICalendarContentDay {
    dayStartWeek: DateTime;
    currentDayStartWeek: DateTime;
    indexWeek: number;
    indexDayWeek: number;
    itemsDataByWeekDay: (ItemType | null)[][];
}

const CalendarContentDay: React.FC<ICalendarContentDay> = ({
    dayStartWeek,
    indexWeek,
    indexDayWeek,
    itemsDataByWeekDay,
    currentDayStartWeek,
}) => {
    const filteredTasks = useSelector<RootState, ItemTaskType[]>((state) => state.calendar.filteredTasks);

    const currentDate = useSelector<RootState, string>((state) => state.calendar.currentDate);

    const currentDay = dayStartWeek.plus({
        days: indexWeek * COUNT_DAY_IN_WEEK + indexDayWeek,
    });
    const value = currentDay.toFormat('dd');

    const taskListForDay = filteredTasks.filter((task) => task.datetime === currentDay.toFormat(FORMAT_DATE));

    const renderHeaderDay = () => {
        return (
            <div
                style={{
                    paddingRight: 5,
                    height: 18,
                    fontWeight: 500,
                    color: '#575757',
                    marginTop: -2,
                }}
            >
                {value}
            </div>
        );
    };

    const renderItemBars = (staticSize?: boolean) => {
        return itemsDataByWeekDay.map((items, index) => {
            const day = indexDayWeek + 1;
            const keyValue = items[indexDayWeek] ? `${day}-${items[indexDayWeek]!.id}` : `${day}-${index}-null`;
            return (
                <CalendarItemMonth
                    key={keyValue}
                    day={day as WeekdayNumbers}
                    item={items[indexDayWeek]}
                    isInteractive={true}
                    staticSize={staticSize}
                    currentDate={currentDayStartWeek.toFormat(FORMAT_DATE)}
                />
            );
        });
    };

    const renderTasks = (isShowMore?: boolean) => {
        const slicedTaskList = !isShowMore
            ? [...taskListForDay].splice(0, MAXIMUM_COUNT_FOR_SHOWING_TASK)
            : [...taskListForDay];

        return slicedTaskList.map((task) => <CalendarTaskMonth key={task.id} task={task} />);
    };

    const renderButtonMore = () => {
        const content = (
            <div>
                {renderItemBars(true)}
                {renderTasks(true)}
            </div>
        );

        return (
            taskListForDay.length > MAXIMUM_COUNT_FOR_SHOWING_TASK && (
                <Flex justify={'center'}>
                    <Popover content={content} title={currentDay.toFormat(FORMAT_DATE_DISPLAY_DAY)}>
                        <div
                            style={{
                                cursor: 'pointer',
                                borderRadius: 10,
                                height: 'clamp(14px, 1.6vw, 18px)',
                                padding: '0 10px',
                                backgroundColor: COLOR_WEEKEND,
                                fontSize: 'clamp(9px, 1.1vw, 12px)',
                            }}
                        >
                            +{taskListForDay.length - MAXIMUM_COUNT_FOR_SHOWING_TASK} more
                        </div>
                    </Popover>
                </Flex>
            )
        );
    };

    return (
        <Flex
            vertical
            justify={'space-between'}
            style={{
                height: 'calc(100% - 1px)',
                borderBottom: BORDER_MAIN,
                backgroundColor: currentDate === currentDay.toFormat(FORMAT_DATE) ? '#f5f5f5' : 'white',
            }}
        >
            <div style={{ textAlign: 'right' }}>
                {renderHeaderDay()}
                {renderItemBars()}
            </div>
            <div style={{ padding: '0 5px', zIndex: 1, paddingBottom: 2 }}>
                {renderTasks()}
                {renderButtonMore()}
            </div>
        </Flex>
    );
};

export default React.memo(CalendarContentDay);
